import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import LogoMark from "@/components/LogoMark";

const links = [
  { href: "#services", label: "Services" },
  { href: "#manifesto", label: "Manifesto" },
  { href: "#work", label: "Work" },
  { href: "#estimate", label: "Estimate" },
];

export default function Nav({ show = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={show ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.3 }}
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-10">
        <a
          href="#top"
          data-testid="nav-brand"
          className="flex items-center gap-3"
        >
          <LogoMark size={40} />
          <span className="hidden font-oswald text-sm uppercase tracking-[0.28em] text-white/80 md:block">
            Construct & Repair
          </span>
        </a>

        <nav className="hidden items-center gap-10 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="group relative font-oswald text-sm uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-white transition-[width] duration-500 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-call-btn"
            href="tel:6103497313"
            className="hidden items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-widest text-white/90 transition hover:border-white/50 md:inline-flex"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--accent)] opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--accent)]" />
            </span>
            24/7 · 610-349-7313
          </a>

          <button
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            onClick={() => setOpen((o) => !o)}
            className="grid h-10 w-10 place-items-center border border-white/15 text-white md:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/95 md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="border-b border-white/5 py-4 font-oswald text-2xl uppercase tracking-tight text-white/90"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:6103497313"
                data-testid="nav-mobile-call"
                className="mt-4 inline-flex items-center gap-2 self-start bg-[color:var(--accent)] px-5 py-3 font-oswald text-sm uppercase tracking-widest text-black"
              >
                <Phone size={14} /> Call 24/7 · 610-349-7313
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
